import { Link } from 'react-router-dom';
import { Head } from '@src/generic';

type StaticPageSection = {
  title: string;
  paragraphs: string[];
};

type StaticPageLink = {
  label: string;
  to?: string;
  href?: string;
};

type StaticPageAsideCard = {
  title: string;
  description?: string;
  links?: StaticPageLink[];
};

type StaticPageLayoutProps = {
  eyebrow: string;
  title: string;
  lead: string;
  pageTitle: string;
  referenceLabel: string;
  referenceValue: string;
  sections: StaticPageSection[];
  sectionsVariant?: 'cards' | 'accordion';
  asideCards?: StaticPageAsideCard[];
};

const StaticPageLinkItem = ({ label, to, href }: StaticPageLink) => {
  if (href) {
    return (
      <a className="catalog-static-page__aside-link" href={href}>
        {label}
      </a>
    );
  }

  return (
    <Link className="catalog-static-page__aside-link" to={to || '/'}>
      {label}
    </Link>
  );
};

const StaticPageLayout = ({
  eyebrow,
  title,
  lead,
  pageTitle,
  referenceLabel,
  referenceValue,
  sections,
  sectionsVariant = 'cards',
  asideCards = [],
}: StaticPageLayoutProps) => (
  <>
    <Head title={pageTitle} />
    <div className="catalog-static-page">
      <section className="catalog-static-page__hero">
        <div className="catalog-static-page__hero-copy">
          <span className="catalog-static-page__eyebrow">{eyebrow}</span>
          <h1 className="catalog-static-page__title">{title}</h1>
          <p className="catalog-static-page__lead">{lead}</p>
        </div>
        <div className="catalog-static-page__reference">
          <span className="catalog-static-page__reference-label">{referenceLabel}</span>
          <strong className="catalog-static-page__reference-value">{referenceValue}</strong>
        </div>
      </section>

      <div className="catalog-static-page__body">
        <article className="catalog-static-page__content">
          {sections.map((section) => (
            sectionsVariant === 'accordion' ? (
              <details key={section.title} className="catalog-static-page__section catalog-static-page__section--collapsible">
                <summary className="catalog-static-page__section-summary">
                  <h2>{section.title}</h2>
                </summary>
                <div className="catalog-static-page__section-panel">
                  {section.paragraphs.map((paragraph) => (
                    <p key={paragraph}>{paragraph}</p>
                  ))}
                </div>
              </details>
            ) : (
              <section key={section.title} className="catalog-static-page__section">
                <h2>{section.title}</h2>
                {section.paragraphs.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </section>
            )
          ))}
        </article>

        <aside className="catalog-static-page__aside">
          {asideCards.map((card) => (
            <section key={card.title} className="catalog-static-page__aside-card">
              <h2>{card.title}</h2>
              {card.description ? <p>{card.description}</p> : null}
              {card.links?.length ? (
                <div className="catalog-static-page__aside-links">
                  {card.links.map((link) => (
                    <StaticPageLinkItem key={`${link.label}-${link.to || link.href}`} {...link} />
                  ))}
                </div>
              ) : null}
            </section>
          ))}
        </aside>
      </div>
    </div>
  </>
);

export default StaticPageLayout;
